import type { ComparisonOp, Condition, ConditionOperand, ConditionValue } from './types';

// Mirrors scenario/conditions.py in the backend.
export const MAX_CONDITION_DEPTH = 4;
export const COMPARISON_OPS: ComparisonOp[] = ['eq', 'ne', 'lt', 'lte', 'gt', 'gte'];
export const OP_SYMBOLS: Record<ComparisonOp, string> = { eq: '=', ne: '≠', lt: '<', lte: '≤', gt: '>', gte: '≥' };

export const GROUP_KINDS = ['all', 'any', 'not'] as const;
export const LEAF_KINDS = ['compare', 'in', 'present', 'empty', 'tool_success'] as const;
export type ConditionKind = (typeof GROUP_KINDS)[number] | (typeof LEAF_KINDS)[number];

type Moment = Extract<ConditionOperand, { at: unknown }>;
type Leaf = Exclude<Condition, { all: unknown } | { any: unknown } | { not: unknown }>;
type ToolSuccessNode = Extract<Condition, { op: 'tool_success' }>;

export const conditionKind = (node: Condition): ConditionKind => {
  if ('all' in node) return 'all';
  if ('any' in node) return 'any';
  if ('not' in node) return 'not';
  return node.op === 'in' || node.op === 'present' || node.op === 'empty' || node.op === 'tool_success'
    ? node.op
    : 'compare';
};

export const isMoment = (operand: ConditionOperand): operand is Moment => 'at' in operand;

const isToolSuccess = (node: Condition): node is ToolSuccessNode => 'op' in node && node.op === 'tool_success';

export const conditionChildren = (node: Condition): Condition[] => {
  if ('all' in node) return node.all;
  if ('any' in node) return node.any;
  if ('not' in node) return [node.not];
  return [];
};

const firstOperand = (node: Condition | undefined): ConditionOperand | undefined => {
  if (!node) return undefined;
  if ('value' in node) return node.value;
  if ('left' in node) return node.left;
  return undefined;
};

const emptyFact = (): ConditionValue => ({ source: 'fact', key: '' });

/** A new node of `kind`. Groups wrap or keep the previous node; leaves keep its first operand. */
export const newCondition = (kind: ConditionKind, previous?: Condition): Condition => {
  const children = previous ? conditionChildren(previous) : [];
  const isGroup = previous !== undefined && children.length > 0;
  const value = firstOperand(previous) ?? emptyFact();
  switch (kind) {
    case 'all':
    case 'any': {
      const items = isGroup ? children : previous ? [previous] : [newCondition('compare')];
      return kind === 'all' ? { all: items } : { any: items };
    }
    case 'not':
      return { not: previous ?? newCondition('compare') };
    case 'in':
      return { op: 'in', value, values: [''] };
    case 'present':
    case 'empty':
      return { op: kind, value };
    case 'tool_success':
      return { op: 'tool_success', tool: '', unchanged: [] };
    case 'compare':
      return { op: 'eq', left: value, right: { source: 'constant', value: '' } };
  }
};

const mapLeaves = (node: Condition, fn: (leaf: Leaf) => Condition): Condition => {
  if ('all' in node) return { all: node.all.map(item => mapLeaves(item, fn)) };
  if ('any' in node) return { any: node.any.map(item => mapLeaves(item, fn)) };
  if ('not' in node) return { not: mapLeaves(node.not, fn) };
  return fn(node);
};

const renameInValue = (value: ConditionValue, from: string, to: string): ConditionValue =>
  value.source === 'fact' && value.key === from ? { ...value, key: to } : value;

const renameInOperand = (operand: ConditionOperand, from: string, to: string): ConditionOperand =>
  isMoment(operand)
    ? { at: { date: renameInValue(operand.at.date, from, to), time: renameInValue(operand.at.time, from, to) } }
    : renameInValue(operand, from, to);

export const renameFactInCondition = (node: Condition, from: string, to: string): Condition =>
  mapLeaves(node, leaf => {
    if (leaf.op === 'tool_success') return { ...leaf, unchanged: leaf.unchanged.map(name => (name === from ? to : name)) };
    if ('left' in leaf) {
      return { ...leaf, left: renameInOperand(leaf.left, from, to), right: renameInOperand(leaf.right, from, to) };
    }
    return { ...leaf, value: renameInOperand(leaf.value, from, to) };
  });

export const renameToolInCondition = (node: Condition, from: string, to: string): Condition =>
  mapLeaves(node, leaf => (leaf.op === 'tool_success' && leaf.tool === from ? { ...leaf, tool: to } : leaf));

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const valueFits = (value: unknown) => isObject(value) && typeof value.source === 'string';

const operandFits = (operand: unknown) =>
  isObject(operand) && ('at' in operand ? isObject(operand.at) && valueFits(operand.at.date) && valueFits(operand.at.time) : valueFits(operand));

/** True when the form editor cannot render the condition; it can still be edited as JSON. */
export const conditionIncompatible = (node: unknown): boolean => {
  if (!isObject(node)) return true;
  if ('all' in node || 'any' in node) {
    const items = node.all ?? node.any;
    return !Array.isArray(items) || items.some(item => conditionIncompatible(item));
  }
  if ('not' in node) return conditionIncompatible(node.not);
  switch (node.op) {
    case 'present':
    case 'empty':
      return !operandFits(node.value);
    case 'in':
      return !operandFits(node.value) || !Array.isArray(node.values);
    case 'tool_success':
      return typeof node.tool !== 'string' || !Array.isArray(node.unchanged);
    default:
      return (
        !COMPARISON_OPS.includes(node.op as ComparisonOp) ||
        !operandFits(node.left) ||
        !operandFits(node.right) ||
        (node.offset !== undefined && typeof node.offset !== 'string')
      );
  }
};

export interface SuccessRef {
  tool: string;
  unchanged: string[];
}

export interface ConditionLinks {
  /** Facts and system values read by operands; hard ones can never be empty when the condition is met. */
  reads: { source: 'fact' | 'system'; key: string; hard: boolean }[];
  /** Successes the condition always needs. */
  success: SuccessRef[];
  /** Groups of successes of which one is enough (`any` of tool_success). */
  alternatives: SuccessRef[][];
  /** Successes that may help but are not needed on every path. */
  softSuccess: SuccessRef[];
}

/**
 * What a condition depends on, for the flow diagram. A comparison or `in` with an empty operand is
 * unknown and never met, even under `not`; `present` needs its value only outside `not`, `empty` only
 * inside. Under `not`, `all` and `any` swap roles (De Morgan), so only conjunctive paths are hard.
 */
export const conditionLinks = (condition: unknown): ConditionLinks => {
  const links: ConditionLinks = { reads: [], success: [], alternatives: [], softSuccess: [] };
  const read = (operand: unknown, hard: boolean) => {
    if (!isObject(operand)) return;
    const values = 'at' in operand && isObject(operand.at) ? [operand.at.date, operand.at.time] : [operand];
    for (const value of values) {
      if (!isObject(value) || (value.source !== 'fact' && value.source !== 'system') || typeof value.key !== 'string') continue;
      if (value.key) links.reads.push({ source: value.source, key: value.key, hard });
    }
  };
  const visit = (node: unknown, positive: boolean, required: boolean) => {
    if (!isObject(node)) return;
    if ('all' in node || 'any' in node) {
      const items = (Array.isArray(node.all) ? node.all : Array.isArray(node.any) ? node.any : []) as Condition[];
      const conjunctive = ('all' in node) === positive || items.length === 1;
      if (!conjunctive && required && positive && items.length > 0 && items.every(item => isObject(item) && isToolSuccess(item))) {
        links.alternatives.push((items as ToolSuccessNode[]).map(({ tool, unchanged }) => ({ tool, unchanged })));
        return;
      }
      for (const item of items) visit(item, positive, required && conjunctive);
      return;
    }
    if ('not' in node) {
      visit(node.not, !positive, required);
      return;
    }
    switch (node.op) {
      case 'present':
        read(node.value, required && positive);
        break;
      case 'empty':
        read(node.value, required && !positive);
        break;
      case 'tool_success':
        if (positive && typeof node.tool === 'string' && node.tool) {
          const ref = { tool: node.tool, unchanged: Array.isArray(node.unchanged) ? (node.unchanged as string[]) : [] };
          (required ? links.success : links.softSuccess).push(ref);
        }
        break;
      case 'in':
        read(node.value, required);
        break;
      default:
        read(node.left, required);
        read(node.right, required);
    }
  };
  visit(condition, true, true);
  return links;
};
